import { useEffect, useState } from "react";
import { useReducedMotion } from "../../../lib/motion/index.js";
import { useI18n } from "../../../lib/useI18n.js";
import { MarkdownPreview } from "../../notes/MarkdownPreview.js";
import { hasMarkdownSyntax } from "../../notes/markdown.js";
import type { useTaskForm } from "../useTaskForm.js";
import { CardFooter } from "./CardFooter.js";
import { ColorSwatchRow } from "./ColorSwatchRow.js";
import { Check } from "./bits.js";
import { useAutoGrow } from "./hooks.js";
import { InkCanvas } from "./InkCanvas.js";
import { LocationField, type LocPick } from "./LocationField.js";
import { MiniMap } from "./MiniMap.js";
import { useSettingsStore } from "../../../stores/index.js";
import { matrixEnabledFor } from "../../../stores/settings.store.js";
import { PriorityMatrix } from "../../priority-matrix/PriorityMatrix.js";
import { matrixDisplayWithUrgency, useDeadlineUrgencyValue } from "../../priority-matrix/useEffectiveMatrixDisplay.js";
import { contrastShade, matrixAccentFromPos, PRIORITY_FLAG_HEX } from "../../task-colors.js";
import { formDeadlineIso } from "../time-helpers.js";
import { NO_TOPIC_COLOR, PRIORITY_PAPER, paperTint, stickerGlyph } from "./palette.js";
import { PriorityRail } from "./PriorityRail.js";
import { ReminderPanel } from "./ReminderPanel.js";
import { RepeatPanel } from "./RepeatPanel.js";
import { SubtasksPanel } from "./SubtasksPanel.js";
import { TimeRow } from "./TimeRow.js";
import { TopicRail } from "./TopicRail.js";
import { useInkPulse } from "./useInkPulse.js";
import { useTopicRail } from "./useTopicRail.js";
import { WashStack, type WashVars } from "./WashStack.js";

interface Props {
  form: ReturnType<typeof useTaskForm>;
  /** Overlay root — portal target for the location dropdown. */
  portalEl: HTMLElement | null;
}

export function PaperTaskCard({ form, portalEl }: Props) {
  const { t } = useI18n();
  const {
    open, editing,
    title, setTitle, notes, setNotes,
    dateStr, setDateStr, allDay, setAllDay,
    hour, setHour, minute, setMinute,
    duration, setDuration,
    priority, setPriority,
    matrixPos, setMatrixPos,
    customColor, setCustomColor,
    isEvent, setIsEvent,
    reminderOffsets, setReminderOffsets,
    tz, setTz, calendarId, setCalendarId,
    topicIds, setTopicIds, labelIds, setLabelIds,
    location, setLocation,
    pendingSubtasks, setPendingSubtasks,
    deadlineStr, setDeadlineStr,
    recurEnabled, setRecurEnabled, recurRule, setRecurRule,
    seriesUntilStr, setSeriesUntilStr,
    error, submitting,
    submit, deleteIt, handleComplete, requestClose,
  } = form;

  const matrixScope = useSettingsStore((s) => s.matrixScope);
  const v2 = useSettingsStore((s) => s.desktopFormVariant) === "modern";
  // Matrix in scope: the full 3×3 sits inline below the title on the card's
  // right side; the flag rail stays visible as a read-only reflection.
  const matrixInline = matrixEnabledFor(matrixScope, isEvent);
  // Urgency auto-derived from the entry's deadline (deadline_at ?? due_at) — the
  // matrix ghost dot then sits at the deadline-implied urgency column.
  const deadlineUrgency = useDeadlineUrgencyValue(
    formDeadlineIso({ isEvent, deadlineStr, dateStr, hour, minute, allDay, tz }),
    tz,
  );
  const topicRail = useTopicRail(topicIds, setTopicIds);
  const { rail, selectedRoot, selectedRootIds, selectedIndex, subSel, selectRoot, toggleSub } = topicRail;

  const priorityObj = PRIORITY_PAPER.find((p) => p.id === priority) ?? PRIORITY_PAPER[0]!;
  const topicWash = topicRail.topicWash ?? NO_TOPIC_COLOR;
  const customWash = customColor ? paperTint(customColor, 0.6) : "var(--pcard)";

  // V2 (modern skin): like the mobile sheet, the card BORDER carries the
  // priority color — the matrix's diagonal gradient at the entry's (possibly
  // deadline-derived) position when the matrix governs, else the vivid flag
  // hue. The wash keeps only the topic band (top) + custom color (body).
  const v2Border = !v2
    ? null
    : matrixInline
      ? matrixAccentFromPos(matrixDisplayWithUrgency(matrixPos, priority, deadlineUrgency))
      : PRIORITY_FLAG_HEX[priority] || null;
  // Readable ink over the v2 topic band (mirrors the mobile sheet's contrast ink).
  const v2Ink = v2 && topicRail.topicWash ? contrastShade(topicRail.topicWash) : null;
  // The color that reads as "the priority accent" on this skin (divider, pulses).
  const priColor = v2 ? v2Border ?? "var(--hairline-2)" : priorityObj.color;

  const washVars: WashVars = {
    // V2 with no topic shows NO band (the paper skin tints neutral instead).
    ["--c-topic"]: v2 && !topicRail.topicWash ? "transparent" : topicWash,
    ["--c-priority"]: priorityObj.color,
    ["--c-custom"]: customWash,
    // A chosen custom color claims more of the card; "none" collapses its region.
    ["--custom-area"]: customColor ? 1.55 : 0.6,
    ...(v2Border ? { ["--c-border"]: v2Border } : null),
    ...(v2Ink ? { ["--v2-ink"]: v2Ink } : null),
  };

  const animOn = !useReducedMotion();
  const pulse = useInkPulse({
    topicId: selectedRoot?.id ?? null,
    topicIndex: selectedIndex,
    topicColor: topicWash,
    priority,
    // V2 blooms with the vivid flag/matrix accent (concrete color — the canvas
    // pigment parser can't resolve var() references).
    priorityColor: v2 ? v2Border ?? priorityObj.color : priorityObj.color,
    customKey: customColor,
    customColor: customWash,
    subSel,
    rail,
  });

  // Reminder panel open-state: defaults to "open when the task has reminders";
  // unchecking the box clears the offsets (= turns reminders off).
  const [remindOverride, setRemindOverride] = useState<boolean | null>(null);
  useEffect(() => { setRemindOverride(null); }, [open, editing]);
  const remindOpen = remindOverride ?? reminderOffsets.length > 0;
  const setRemindOpen = (v: boolean) => {
    setRemindOverride(v);
    if (!v) setReminderOffsets([]);
  };

  const [locCoords, setLocCoords] = useState<LocPick | null>(null);
  useEffect(() => { setLocCoords(null); }, [open, editing]);
  const notesRef = useAutoGrow(notes, 4, !!location.trim() || matrixInline);
  const showNotesPreview = notes.trim().length > 0 && hasMarkdownSyntax(notes);

  const isMac = typeof navigator !== "undefined" && /mac/i.test(navigator.platform);

  return (
    <div className="card-wrap">
      <div className="card" style={washVars}>
        <WashStack vars={washVars} animOn={animOn} />
        {animOn && (
          <div className="ink-layer">
            <InkCanvas pulse={pulse} />
          </div>
        )}
        <div className="spine" />

        <PriorityRail value={priority} onChange={setPriority} readOnly={form.matrixInScope} />
        <TopicRail
          topics={rail}
          values={selectedRootIds}
          onChange={selectRoot}
          subSel={subSel}
          onToggleSub={toggleSub}
        />

        <div className="kicker corner-kicker" role="tablist">
          <button type="button" role="tab" className={!isEvent ? "is-on" : ""} onClick={() => setIsEvent(false)}>
            {t("taskForm.task")}
          </button>
          <button type="button" role="tab" className={isEvent ? "is-on" : ""} onClick={() => setIsEvent(true)}>
            {t("taskForm.event")}
          </button>
        </div>

        <div className="body">
          <div className="header">
            <div
              className={"sticker" + (selectedRoot ? "" : " is-empty")}
              title={selectedRoot?.name ?? t("taskForm.noTopic")}
              style={{
                background: `color-mix(in oklch, ${topicWash}, black 30%)`,
                ["--sticker-bg" as string]: `color-mix(in oklch, ${topicWash}, black 30%)`,
                border: `3px solid color-mix(in oklch, ${topicWash}, black 42%)`,
                boxShadow: "0 4px 10px -4px color-mix(in oklch, var(--ink) 22%, transparent)",
              }}
            >
              {selectedRoot
                ? selectedRoot.icon ?? stickerGlyph(selectedRoot.name)
                : <span className="sticker-empty-mark" aria-hidden="true" />}
            </div>
            <input
              className="title-input"
              placeholder={isEvent ? t("taskForm.untitledEvent") : t("taskForm.untitledTask")}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
            />
          </div>

          <div className="loc-divider">
            <div className="header-loc">
              <LocationField
                value={location}
                onChange={(v) => { setLocation(v); setLocCoords(null); }}
                onPick={(label, coords) => { setLocation(label); setLocCoords(coords); }}
                portalEl={portalEl}
              />
            </div>
            <hr
              className="divider"
              style={{
                background: `linear-gradient(to right, transparent, color-mix(in oklch, ${priColor}, var(--ink) 16%) 14%, color-mix(in oklch, ${priColor}, var(--ink) 16%) 86%, transparent)`,
                filter: `drop-shadow(0 2px 4px color-mix(in oklch, ${priColor} 60%, transparent))`,
                height: "2px",
                border: "none",
              }}
            />
          </div>

          <div className={"mid-split" + (matrixInline ? " has-matrix" : "")}>
            <div className="mid-main">
              <div className={"notes-row" + (location.trim() ? " has-map" : "")}>
                <div className="field notes-field">
                  <textarea
                    ref={notesRef}
                    className="notes"
                    placeholder={t("taskForm.notesPlaceholder")}
                    rows={1}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>
                {location.trim() && (
                  <MiniMap label={location.trim()} pinColor={topicWash} knownCoords={locCoords} />
                )}
              </div>
              {showNotesPreview && (
                <div className="notes-preview" aria-label={t("taskForm.markdownPreview")}>
                  <div className="notes-preview-label">{t("taskForm.markdownPreview")}</div>
                  <MarkdownPreview value={notes} className="notes-preview-body" />
                </div>
              )}

              <TimeRow
                dateStr={dateStr}
                hour={hour}
                minute={minute}
                allDay={allDay}
                duration={duration}
                isEvent={isEvent}
                deadlineStr={deadlineStr}
                onDate={(d, h, m) => { setDateStr(d); setHour(h); setMinute(m); }}
                onAllDay={setAllDay}
                onDuration={setDuration}
                onDeadline={setDeadlineStr}
              />
            </div>
            {matrixInline && (
              <div className="matrix-inline">
                <div className="field-label">{t("matrix.title")}</div>
                <PriorityMatrix
                  value={matrixPos}
                  priority={priority}
                  onChange={setMatrixPos}
                  deadlineUrgency={deadlineUrgency}
                  hidden={form.matrixHidden}
                  onHiddenChange={form.setMatrixHidden}
                />
              </div>
            )}
          </div>

          <div className="opt-row">
            <Check checked={recurEnabled} onChange={setRecurEnabled}>{t("taskForm.repeating")}</Check>
            <Check checked={remindOpen} onChange={setRemindOpen}>{t("taskForm.reminder")}</Check>
            <span className="opt-spacer" />
            <ColorSwatchRow value={customColor} onChange={setCustomColor} />
          </div>

          <RepeatPanel
            open={recurEnabled}
            rule={recurRule}
            onRule={setRecurRule}
            untilStr={seriesUntilStr}
            onUntil={setSeriesUntilStr}
          />

          <ReminderPanel open={remindOpen} offsets={reminderOffsets} onChange={setReminderOffsets} />

          <SubtasksPanel editing={editing} pending={pendingSubtasks} onPendingChange={setPendingSubtasks} />

          <CardFooter
            editing={editing}
            isEvent={isEvent}
            calendarId={calendarId}
            onCalendar={setCalendarId}
            labelIds={labelIds}
            onLabels={setLabelIds}
            tz={tz}
            onTz={setTz}
            error={error}
            submitting={submitting}
            onCancel={requestClose}
            onSubmit={() => void submit()}
            onDelete={() => void deleteIt()}
            onComplete={() => void handleComplete()}
          />
        </div>
      </div>

      <div className="helper">{t("taskForm.helper", { mod: isMac ? "⌘" : "Ctrl" })}</div>
    </div>
  );
}
